import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import { notificationsService } from "../../services/notifications.service";
import { Notification } from "../../types/notification";
import { Permission } from "../../permissions/permission.enum";
import PermissionRoute from "../../components/PermissionRoute";
import Button from "../../components/ui/Button";
import DataTable, {
  Column,
  DataTableFetchParams,
  DataTableFilterDef,
} from "../../components/ui/DataTable";

const UserNotifications = () => {
  const { t } = useTranslation("common");
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    const isReadValue = params.filters?.isRead;
    const parsedIsRead =
      isReadValue === "true"
        ? true
        : isReadValue === "false"
          ? false
          : undefined;
    const res = await notificationsService.listUser({
      page: params.page,
      take: params.pageSize,
      isRead: parsedIsRead,
    });
    return {
      data: res.data,
      total: res.meta.itemCount,
      totalPages: res.meta.pageCount,
    };
  }, []);

  const markRead = async (id: string) => {
    try {
      await notificationsService.markUserRead(id);
      setTableRefreshKey((prev) => prev + 1);
    } catch {
      // Best-effort; DataTable handles fetch errors
    }
  };

  const columns: Column<Notification>[] = [
    {
      key: "title",
      header: t("title"),
      className: "font-medium text-gray-900 dark:text-gray-100",
    },
    {
      key: "message",
      header: t("message"),
      className: "text-gray-500 dark:text-gray-400 max-w-xs truncate",
    },
    {
      key: "createdAt",
      header: t("created_at"),
      render: (n) => new Date(n.createdAt).toLocaleString(),
    },
    {
      key: "status",
      header: t("status"),
      render: (n) =>
        n.isRead ? (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            {t("read")}
          </span>
        ) : (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            {t("unread")}
          </span>
        ),
    },
    {
      key: "actions",
      header: t("actions"),
      className: "text-right",
      render: (n) =>
        !n.isRead && (
          <Button size="sm" onClick={() => markRead(n.id)}>
            {t("mark_as_read")}
          </Button>
        ),
    },
  ];

  const filterDefs: DataTableFilterDef[] = [
    {
      key: "isRead",
      label: t("status"),
      type: "select",
      options: [
        { label: t("all"), value: "" },
        { label: t("unread"), value: "false" },
        { label: t("read"), value: "true" },
      ],
    },
  ];

  return (
    <PermissionRoute permission={Permission.USER_NOTIFICATIONS_READ}>
      <Layout title={t("user_notifications")}>
        <div className="space-y-4 h-full flex flex-col">
          <Card className="p-4 shadow-sm flex-shrink-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t("user_notifications")}
              </h1>
            </div>
          </Card>

          <DataTable
            className="flex-1 min-h-0"
            columns={columns}
            onFetch={handleFetch}
            filterDefs={filterDefs}
            initialState={{
              sortBy: "createdAt",
              sortOrder: "DESC",
              pageSize: 50,
            }}
            refreshKey={tableRefreshKey}
            emptyMessage={t("noData")}
          />
        </div>
      </Layout>
    </PermissionRoute>
  );
};

export default UserNotifications;
